import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, ShoppingBag, Upload, Wand2, Gift } from 'lucide-react';
import { api } from '@/lib/api';
import { proxyImg } from '@/lib/imageProxy';
import { useMall } from '@/lib/mall';
import MarqueeRibbon from '@/components/MarqueeRibbon';
import MallBannerCarousel from '@/components/MallBannerCarousel';

export default function Home() {
    const { selectedMall, allMalls, choose } = useMall();
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [featuredOffers, setFeaturedOffers] = useState([]);
    const [scrapedCategories, setScrapedCategories] = useState([]);
    const [scrapedBanners, setScrapedBanners] = useState([]);

    useEffect(() => {
        // Featured products now come from the real scraped Inorbit catalog
        const qs = new URLSearchParams({ page_size: '8', sort: 'discount' });
        if (selectedMall) qs.set('mall_slug', selectedMall.slug);
        api.get(`/catalog/products?${qs.toString()}`).then(({ data }) => {
            setFeaturedProducts(data.items);
        }).catch(() => {
            const mallParam = selectedMall ? `?mall=${selectedMall.slug}&limit=8` : '?limit=8';
            api.get(`/products${mallParam}`).then(({ data }) => setFeaturedProducts(data)).catch(() => {});
        });
        api.get(`/offers?featured=true&limit=4${selectedMall ? `&mall=${selectedMall.slug}` : ''}`).then(({ data }) => setFeaturedOffers(data)).catch(() => {});

        // Real scraped categories + banners — both scoped to selected mall
        const catQs = selectedMall ? `?mall_slug=${selectedMall.slug}` : '';
        api.get(`/catalog/categories${catQs}`).then(({ data }) => {
            const seen = new Map();
            data.forEach((c) => {
                if (!c.image) return;
                const key = c.url_key;
                const cur = seen.get(key);
                const total = c.total_products || 0;
                if (!cur || total > (cur.total_products || 0)) seen.set(key, c);
            });
            setScrapedCategories(Array.from(seen.values()).slice(0, 8));
        }).catch(() => {});
        api.get(`/catalog/banners${catQs}`).then(({ data }) => {
            const seen = new Set();
            const uniq = data.filter((b) => seen.has(b.image) ? false : (seen.add(b.image), true));
            setScrapedBanners(uniq.slice(0, 8));
        }).catch(() => {});
    }, [selectedMall]);

    return (
        <div data-testid="home-page">
            {/* SLIM MALL RIBBON — replaces the giant 'One mall identity' hero per latest direction */}
            <section className="relative -mt-20 pt-24 pb-6 bg-inorbit-blue-deeper overflow-hidden" data-testid="hero-section">
                <div className="absolute -top-32 -right-32 w-[420px] h-[420px] border-2 border-inorbit-yellow/15 rounded-full orbit-slow" />
                <div className="absolute -bottom-24 -left-24 w-[340px] h-[340px] border border-inorbit-orange/20 rounded-full orbit-fast" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                        <div className="flex flex-wrap items-end justify-between gap-6 mb-6" data-testid="hero-tagline">
                            <div>
                                <div className="overline text-inorbit-yellow mb-2 flex items-center gap-3">
                                    <MapPin size={12} strokeWidth={2.4} />
                                    {selectedMall ? `${selectedMall.city} — ${selectedMall.short_name}` : "India's Premium Mall Network"}
                                </div>
                                <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight text-white optical-shift" style={{ color: 'white' }}>
                                    {selectedMall ? <>Welcome to <span className="text-inorbit-yellow">{selectedMall.short_name}</span>.</> : 'Welcome to Inorbit.'}{' '}
                                    <span className="script-tag text-inorbit-yellow text-4xl sm:text-6xl md:text-7xl inline-block">Live an Experience.</span>
                                </h1>
                            </div>
                            {selectedMall && (
                                <div className="flex items-center gap-3" data-testid="hero-mall-cta">
                                    <Link to="/shop" className="btn-yellow" data-testid="shop-now-btn">
                                        Shop {selectedMall.short_name} <ArrowRight size={14} />
                                    </Link>
                                    <Link to={`/mall/${selectedMall.slug}`} className="btn-outline border-white text-white hover:bg-white hover:text-inorbit-blue" data-testid="enter-mall-btn">
                                        Visit Mall
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Compact mall switcher — chips */}
                        <div className="flex flex-wrap items-center gap-2 pt-2" data-testid="hero-mall-chips">
                            <span className="text-[10px] tracking-[0.22em] uppercase text-white/55 mr-2">Switch mall</span>
                            {allMalls.map((m) => (
                                <button
                                    key={m.id}
                                    onClick={() => choose(m.slug)}
                                    className={`px-4 py-1.5 rounded-full text-xs tracking-wider uppercase font-semibold transition-all ${
                                        selectedMall?.slug === m.slug
                                            ? 'bg-inorbit-yellow text-inorbit-blue border-2 border-inorbit-yellow'
                                            : 'bg-white/5 text-white border-2 border-white/15 hover:border-inorbit-yellow hover:text-inorbit-yellow'
                                    }`}
                                    data-testid={`hero-mall-${m.slug}`}
                                >
                                    {m.short_name}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* MALL BILLBOARD CAROUSEL — real Inorbit campaign banners, scoped to selectedMall */}
            <MallBannerCarousel banners={scrapedBanners} mallName={selectedMall?.short_name} mallSlug={selectedMall?.slug} />

            {/* Editorial marquee under carousel */}
            <MarqueeRibbon testId="hero-marquee" />

            {/* (legacy section anchor — removed) */}

            {/* SHOP BY CATEGORY — real scraped from Inorbit */}
            {scrapedCategories.length > 0 && (
                <section className="py-20 bg-cream" data-testid="category-showcase">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                            <div>
                                <div className="overline mb-3">Shop by Category</div>
                                <h2 className="font-serif text-4xl md:text-5xl text-inorbit-blue leading-tight optical-shift">
                                    Browse <span className="script-tag text-inorbit-orange script-overlap">every aisle</span>
                                </h2>
                            </div>
                            <Link to="/shop" className="btn-link" data-testid="all-categories">View All <ArrowRight size={14} /></Link>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger dim-siblings">
                            {scrapedCategories.map((c) => (
                                <Link to={`/shop?category=${c.url_key}`} key={`${c.mall_slug}-${c.url_key}`} className="block aspect-[4/5] relative overflow-hidden bg-inorbit-blue group rounded-md" data-testid={`cat-${c.url_key}`}>
                                    <img src={proxyImg(c.image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-inorbit-blue-deeper/95 via-inorbit-blue/40 to-transparent" />
                                    <div className="absolute bottom-0 inset-x-0 p-5">
                                        <div className="font-serif text-2xl text-white leading-tight">{c.name}</div>
                                        {c.total_products && <div className="text-[10px] tracking-[0.22em] uppercase text-inorbit-yellow font-semibold mt-2">{Number(c.total_products).toLocaleString('en-IN')} Products</div>}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FEATURED PRODUCTS — context-aware */}
            <section className="py-24 md:py-28" data-testid="featured-products-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
                        <div>
                            <div className="overline mb-4">Shop The Edit</div>
                            <h2 className="font-serif text-5xl md:text-6xl text-inorbit-blue leading-tight">
                                {selectedMall ? <>Trending at <span className="script-tag text-inorbit-orange">{selectedMall.short_name}</span></> : 'Trending across Inorbit'}
                            </h2>
                        </div>
                        <Link to="/shop" className="btn-link" data-testid="view-all-products">Shop All <ArrowRight size={14} /></Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 stagger dim-siblings">
                        {featuredProducts.map((p, i) => (
                            <motion.div
                                key={p.id}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.04 }}
                            >
                                <Link to={`/product/${p.id}`} className="card-luxury block img-zoom group" data-testid={`product-card-${p.id}`}>
                                    <div className="aspect-[4/5] overflow-hidden bg-cream">
                                        <img src={proxyImg(p.image || p.images?.[0])} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                                    </div>
                                    <div className="p-5">
                                        <div className="overline text-inorbit-orange text-[10px] mb-1 truncate">{p.brand || p.brand_name}</div>
                                        <div className="text-sm text-inorbit-blue font-medium leading-snug line-clamp-2 mb-3">{p.name}</div>
                                        <div className="flex items-baseline gap-2">
                                            <span className="font-semibold text-inorbit-blue">₹{Number(p.price).toLocaleString('en-IN')}</span>
                                            {p.mrp > p.price && (
                                                <>
                                                    <span className="text-xs text-text-tertiary line-through">₹{Number(p.mrp).toLocaleString('en-IN')}</span>
                                                    <span className="text-xs text-inorbit-orange font-semibold">{p.discount_pct || Math.round((1 - p.price / p.mrp) * 100)}% off</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY INORBIT ONE — colorful blocks */}
            <section className="py-20 bg-cream" data-testid="why-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-center mb-14">
                        <div className="overline mb-4">Membership That Travels</div>
                        <h2 className="font-serif text-5xl md:text-6xl text-inorbit-blue leading-tight">One card. <span className="script-tag text-inorbit-orange">Every mall.</span></h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { icon: ShoppingBag, t: 'Shop Brands', d: 'Order from 500+ brands across all Inorbit malls — multi-brand cart.', to: '/shop', color: 'bg-inorbit-blue text-white' },
                            { icon: Upload, t: 'Upload Bills', d: 'AI OCR reads your bill and credits points automatically.', to: '/bills', color: 'bg-inorbit-yellow text-inorbit-blue' },
                            { icon: Gift, t: 'Redeem Rewards', d: 'Vouchers, dining, movies, gold, experiences.', to: '/rewards', color: 'bg-inorbit-orange text-white' },
                            { icon: Wand2, t: 'AI Concierge', d: 'Your personal shopping companion, 24/7.', to: '/concierge', color: 'bg-white border-2 border-inorbit-blue text-inorbit-blue' },
                        ].map((f) => (
                            <Link to={f.to} key={f.t} className={`${f.color} p-8 group block hover:scale-[1.02] transition-transform rounded-md`} data-testid={`why-${f.t.toLowerCase().replace(/\s+/g, '-')}`}>
                                <f.icon size={28} strokeWidth={1.5} className="mb-6" />
                                <div className="font-serif text-2xl mb-3" style={{ color: 'inherit' }}>{f.t}</div>
                                <div className="text-sm opacity-90 leading-relaxed">{f.d}</div>
                                <div className="text-xs uppercase tracking-widest mt-6 opacity-80">Explore →</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* OFFERS */}
            {featuredOffers.length > 0 && (
                <section className="py-24" data-testid="offers-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
                            <div>
                                <div className="overline mb-4">This Week</div>
                                <h2 className="font-serif text-5xl text-inorbit-blue leading-tight">Live offers <span className="script-tag text-inorbit-orange">just for you.</span></h2>
                            </div>
                            <Link to="/offers" className="btn-link">All Offers <ArrowRight size={14} /></Link>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                            {featuredOffers.map((o) => (
                                <Link to={`/offers#${o.id}`} key={o.id} className="card-luxury block img-zoom" data-testid={`offer-${o.id}`}>
                                    <div className="aspect-[4/3] overflow-hidden"><img src={o.image_url} alt="" className="w-full h-full object-cover" /></div>
                                    <div className="p-5">
                                        <div className="overline text-inorbit-orange text-[10px] mb-2">{o.brand_name}</div>
                                        <div className="font-serif text-lg text-inorbit-blue leading-snug">{o.title}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FINAL CTA */}
            <section className="py-32 bg-inorbit-gradient text-white text-center relative overflow-hidden" data-testid="final-cta">
                <div className="absolute -top-20 left-1/4 w-[400px] h-[400px] border-2 border-inorbit-yellow/15 rounded-full orbit-slow" />
                <div className="relative max-w-3xl mx-auto px-6">
                    <div className="overline text-inorbit-yellow mb-6">Become a Member</div>
                    <h2 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight" style={{ color: 'white' }}>
                        Welcome to <span className="script-tag text-inorbit-yellow text-7xl md:text-8xl">Inorbit One.</span>
                    </h2>
                    <p className="mt-8 text-white/80 leading-relaxed text-lg">
                        Join today. Get 500 welcome points. Start earning across every Inorbit mall.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link to="/register" className="btn-yellow" data-testid="final-cta-register">Become a Member</Link>
                        <Link to="/login" className="btn-outline border-white text-white hover:bg-white hover:text-inorbit-blue" data-testid="final-cta-signin">Sign In</Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
